import { useState, FormEvent, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { useEvent } from '../store';
import { useAuth } from '../store';
import { processFightResult } from '../utils/result';
import { saveEvent, isUltraAdmin } from '../utils';
import { saveTemplate } from '../services/firestore';
import { ResultScraper } from '../components/admin';
import type { Fight, FightResult } from '../types';

/**
 * Enter Result Page
 * Admin page for entering fight results (or editing existing results)
 * ONLY WORKS FOR TEMPLATES - Ultra-admin only
 * Results propagate to ALL events using this template
 */
export function EnterResultPage() {
  const { eventId, fightId } = useParams<{ eventId: string; fightId: string }>();
  const navigate = useNavigate();
  const { event, getFight, updateFight, bets, participants, updateBet, updateParticipant, updateLeaderboard } = useEvent();
  const { currentUser } = useAuth();
  
  const [selectedWinner, setSelectedWinner] = useState<string>('');
  const [method, setMethod] = useState<'ko_tko' | 'submission' | 'decision_unanimous' | 'decision_majority' | 'decision_split' | 'dq' | 'draw' | 'no_contest'>('ko_tko');
  const [wentTheDistance, setWentTheDistance] = useState<boolean | null>(null);
  const [round, setRound] = useState<string>('');
  const [timeInRound, setTimeInRound] = useState<string>('');
  const [error, setError] = useState('');
  const [isEditing, setIsEditing] = useState(false);

  // Block access for non-templates or non-ultra-admins
  if (event && !event.isTemplate) {
    return (
      <div className="min-h-screen bg-primary-bg flex items-center justify-center p-4">
        <Card padding="lg">
          <h2 className="text-xl font-bold text-primary-text mb-4">Access Denied</h2>
          <p className="text-secondary-text mb-4">
            Results cannot be entered on event instances. They are managed through the template and propagate automatically.
          </p>
          <Button onClick={() => navigate(`/event/${eventId}`)}>
            Back to Event
          </Button>
        </Card>
      </div>
    );
  }

  if (currentUser && !isUltraAdmin(currentUser)) {
    return (
      <div className="min-h-screen bg-primary-bg flex items-center justify-center p-4">
        <Card padding="lg">
          <h2 className="text-xl font-bold text-primary-text mb-4">Ultra Admin Only</h2>
          <p className="text-secondary-text mb-4">
            Only ultra-admins can enter results on templates.
          </p>
          <Button onClick={() => navigate('/games')}>
            Back to Games
          </Button>
        </Card>
      </div>
    );
  }

  // Load existing result if editing a completed fight
  useEffect(() => {
    if (fightId) {
      const fight = getFight(fightId);
      if (fight && fight.result && fight.status === 'completed') {
        setIsEditing(true);
        setSelectedWinner(fight.result.winnerId);
        setMethod(fight.result.method);
        if (fight.result.wentTheDistance !== undefined) {
          setWentTheDistance(fight.result.wentTheDistance);
        }
        if (fight.result.round !== undefined) {
          setRound(fight.result.round.toString());
        }
        if (fight.result.timeInRound) {
          setTimeInRound(fight.result.timeInRound);
        }
      }
    }
  }, [fightId, getFight]);

  // Auto-set "went the distance" based on method
  useEffect(() => {
    // Decision or Draw = always went the distance
    if (method.includes('decision') || method === 'draw') {
      setWentTheDistance(true);
      setRound('');
      setTimeInRound('');
    }
    // KO/TKO, Submission, DQ = never went the distance (need round/time)
    else if (method === 'ko_tko' || method === 'submission' || method === 'dq') {
      setWentTheDistance(false);
      // Still need round/time
    }
    // No Contest = special case, distance not applicable (refund)
    else if (method === 'no_contest') {
      setWentTheDistance(false);
      setRound('');
      setTimeInRound('');
    }
  }, [method]);

  // Handle scraped result from ResultScraper
  const handleScrapedResult = (scrapedResult: FightResult) => {
    setSelectedWinner(scrapedResult.winnerId);
    setMethod(scrapedResult.method);
    if (scrapedResult.wentTheDistance !== undefined) {
      setWentTheDistance(scrapedResult.wentTheDistance);
    }
    if (scrapedResult.round !== undefined) {
      setRound(scrapedResult.round.toString());
    }
    if (scrapedResult.timeInRound) {
      setTimeInRound(scrapedResult.timeInRound);
    }
    // Clear any previous errors
    setError('');
  };

  if (!fightId) {
    navigate(`/admin/${eventId}`);
    return null;
  }

  const fight = getFight(fightId);
  if (!fight) {
    return (
      <div className="min-h-screen bg-primary-bg flex items-center justify-center p-4">
        <Card padding="lg">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-primary-text mb-4">
              Fight Not Found
            </h1>
            <Button variant="secondary" onClick={() => navigate(`/admin/${eventId}`)}>
              Back to Admin Panel
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  const validateTimeFormat = (time: string): boolean => {
    // Format should be MM:SS (e.g., "1:53", "5:00")
    const timeRegex = /^([0-4]?[0-9]|5):[0-5][0-9]$/;
    if (!timeRegex.test(time)) return false;
    
    const [minutes, seconds] = time.split(':').map(Number);
    if (minutes < 0 || minutes > 5) return false;
    if (minutes === 5 && seconds > 0) return false; // Can't be 5:01 or more
    if (minutes === 0 && seconds === 0) return false; // Can't be 0:00
    
    return true;
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');

    if (!selectedWinner) {
      setError('Please select a winner');
      return;
    }

    // Validate method selection
    if (selectedWinner !== 'draw' && selectedWinner !== 'no_contest') {
      if (!method || (method !== 'ko_tko' && method !== 'submission' && method !== 'decision_unanimous' && method !== 'decision_majority' && method !== 'decision_split' && method !== 'dq')) {
        setError('Please select a result method');
        return;
      }
    }

    // Distance is now automatic for ALL methods - no need to validate user choice

    // No Contest is a refund - no round/time needed
    if (method === 'no_contest') {
      // Skip round/time validation
    }
    // Decision/Draw went the distance - no round/time needed
    else if (method.includes('decision') || method === 'draw') {
      // Skip round/time validation
    }
    // DQ or finish before distance - need round and time
    else if (!wentTheDistance) {
      const roundNum = parseInt(round, 10);
      if (isNaN(roundNum) || roundNum < 1 || roundNum > 5) {
        setError('Please enter a valid round (1-5)');
        return;
      }
      
      if (!timeInRound || !validateTimeFormat(timeInRound)) {
        setError('Please enter a valid time in round (format: MM:SS, e.g., 1:53)');
        return;
      }
    }

    if (!event) {
      setError('Event not found');
      return;
    }

    try {
      // Build result object
      const result: FightResult = {
        winnerId: selectedWinner,
        method,
        wentTheDistance: wentTheDistance || false,
      };

      if (!wentTheDistance) {
        result.round = parseInt(round, 10);
        result.timeInRound = timeInRound;
      }

      // Update fight with result
      const updatedFight: Fight = {
        ...fight,
        status: 'completed',
        result,
      };

      // For templates: ONLY save the fight result (no bet processing)
      // Bet processing will happen automatically in event instances via polling
      if (event.isTemplate) {
        console.log('[EnterResultPage] Template result saved - will propagate to all events');
        
        // Update fight in context
        updateFight(fightId, {
          status: 'completed',
          result,
        });

        // Save updated template - USE saveTemplate NOT saveEvent!
        const updatedTemplate = {
          ...event,
          fights: event.fights.map((f) =>
            f.id === fightId ? updatedFight : f
          ),
        };
        await saveTemplate(updatedTemplate);
        
        // Navigate back to admin panel
        navigate(`/admin/${eventId}`);
        return;
      }

      // For regular events (NOT templates): Process bet outcomes normally
      const { updatedParticipants, updatedBets } = processFightResult(
        updatedFight,
        bets,
        participants
      );

      // Update fight in event
      updateFight(fightId, {
        status: 'completed',
        result,
      });

      // Update all affected bets
      updatedBets.forEach((bet) => {
        const originalBet = bets.find((b) => b.id === bet.id);
        if (originalBet && originalBet.status !== bet.status) {
          updateBet(bet.id, {
            status: bet.status,
            payout: bet.payout,
          });
        }
      });

      // Update all affected participants
      updatedParticipants.forEach((participant) => {
        const originalParticipant = participants.find((p) => p.id === participant.id);
        if (originalParticipant) {
          // Check if balance changed
          if (
            originalParticipant.winningsBalance !== participant.winningsBalance ||
            originalParticipant.totalAvailable !== participant.totalAvailable
          ) {
            updateParticipant(participant.id, {
              winningsBalance: participant.winningsBalance,
              totalAvailable: participant.totalAvailable,
              bets: participant.bets,
            });
          }
        }
      });

      // Update leaderboard
      updateLeaderboard();

      // Save to localStorage
      const updatedEvent = {
        ...event,
        fights: event.fights.map((f) =>
          f.id === fightId ? updatedFight : f
        ),
        participants: updatedParticipants,
      };
      await saveEvent(updatedEvent);

      // Navigate back to admin panel
      navigate(`/admin/${eventId}`);
    } catch (err) {
      setError('Failed to enter result. Please try again.');
      console.error('Error entering result:', err);
    }
  };

  return (
    <div className="min-h-screen bg-primary-bg pb-20">
      {/* Header */}
      <div className="bg-secondary-bg border-b border-border p-4 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <h1 className="text-xl font-bold text-primary-text">{isEditing ? 'Edit Result' : 'Enter Result'}</h1>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => navigate(`/admin/${eventId}`)}
          >
            Cancel
          </Button>
        </div>
      </div>

      <div className="max-w-2xl mx-auto p-4">
        {/* Result Scraper */}
        <ResultScraper fight={fight} onResultScraped={handleScrapedResult} />

        <Card padding="lg">
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-primary-text mb-2">
              {fight.fighters[0]?.name || 'TBD'} vs {fight.fighters[1]?.name || 'TBD'}
            </h2>
            <p className="text-sm text-secondary-text">
              Enter the result of this fight (or use scraped result above)
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Winner Selection */}
            <div>
              <label className="block text-sm font-medium text-primary-text mb-3">
                Winner
              </label>
              <div className="space-y-2">
                {fight.fighters.map((fighter, index) => (
                  <button
                    key={fighter.id}
                    type="button"
                    onClick={() => {
                      setSelectedWinner(fighter.id);
                      if (method === 'draw' || method === 'no_contest') {
                        setMethod('ko_tko');
                      }
                    }}
                    className={`w-full p-4 rounded-lg border-2 transition-colors text-left ${
                      selectedWinner === fighter.id
                        ? 'border-primary-blue bg-primary-blue/10'
                        : 'border-border bg-tertiary-bg hover:border-input-focus'
                    }`}
                  >
                    <div className="font-medium text-primary-text">Fighter {index + 1}: {fighter.name}</div>
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => {
                    setSelectedWinner('draw');
                    setMethod('draw');
                  }}
                  className={`w-full p-4 rounded-lg border-2 transition-colors text-left ${
                    selectedWinner === 'draw'
                      ? 'border-primary-blue bg-primary-blue/10'
                      : 'border-border bg-tertiary-bg hover:border-input-focus'
                  }`}
                >
                  <div className="font-medium text-primary-text">Draw</div>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedWinner('no_contest');
                    setMethod('no_contest');
                  }}
                  className={`w-full p-4 rounded-lg border-2 transition-colors text-left ${
                    selectedWinner === 'no_contest'
                      ? 'border-primary-blue bg-primary-blue/10'
                      : 'border-border bg-tertiary-bg hover:border-input-focus'
                  }`}
                >
                  <div className="font-medium text-primary-text">No Contest</div>
                </button>
              </div>
            </div>

            {/* Result Method Selection - Only show if not Draw or No Contest */}
            {selectedWinner !== 'draw' && selectedWinner !== 'no_contest' && selectedWinner !== '' && (
              <div>
                <label className="block text-sm font-medium text-primary-text mb-3">
                  Result Method
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setMethod('ko_tko')}
                    className={`p-3 rounded-lg border-2 transition-colors ${
                      method === 'ko_tko'
                        ? 'border-primary-blue bg-primary-blue/10'
                        : 'border-border bg-tertiary-bg hover:border-input-focus'
                    }`}
                  >
                    <div className="font-medium text-primary-text text-sm">KO/TKO</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setMethod('submission')}
                    className={`p-3 rounded-lg border-2 transition-colors ${
                      method === 'submission'
                        ? 'border-primary-blue bg-primary-blue/10'
                        : 'border-border bg-tertiary-bg hover:border-input-focus'
                    }`}
                  >
                    <div className="font-medium text-primary-text text-sm">Submission</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setMethod('decision_unanimous')}
                    className={`p-3 rounded-lg border-2 transition-colors ${
                      method === 'decision_unanimous'
                        ? 'border-primary-blue bg-primary-blue/10'
                        : 'border-border bg-tertiary-bg hover:border-input-focus'
                    }`}
                  >
                    <div className="font-medium text-primary-text text-sm">Decision - Unanimous</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setMethod('decision_majority')}
                    className={`p-3 rounded-lg border-2 transition-colors ${
                      method === 'decision_majority'
                        ? 'border-primary-blue bg-primary-blue/10'
                        : 'border-border bg-tertiary-bg hover:border-input-focus'
                    }`}
                  >
                    <div className="font-medium text-primary-text text-sm">Decision - Majority</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setMethod('decision_split')}
                    className={`p-3 rounded-lg border-2 transition-colors ${
                      method === 'decision_split'
                        ? 'border-primary-blue bg-primary-blue/10'
                        : 'border-border bg-tertiary-bg hover:border-input-focus'
                    }`}
                  >
                    <div className="font-medium text-primary-text text-sm">Decision - Split</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setMethod('dq')}
                    className={`p-3 rounded-lg border-2 transition-colors ${
                      method === 'dq'
                        ? 'border-primary-blue bg-primary-blue/10'
                        : 'border-border bg-tertiary-bg hover:border-input-focus'
                    }`}
                  >
                    <div className="font-medium text-primary-text text-sm">Disqualification (DQ)</div>
                  </button>
                </div>
              </div>
            )}

            {/* Fight End Section */}
            {selectedWinner !== '' && (
              <div>
                <label className="block text-sm font-medium text-primary-text mb-3">
                  Fight End
                </label>
                
                {/* No Contest - Special case, refund */}
                {(selectedWinner === 'no_contest' || method === 'no_contest') && (
                  <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                    <div className="font-medium text-yellow-400">No Contest</div>
                    <div className="text-xs text-secondary-text mt-1">All bets will be refunded</div>
                  </div>
                )}
                
                {/* Draw - Always goes the distance */}
                {selectedWinner === 'draw' && (
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                    <div className="font-medium text-blue-400">Draw - Went the Distance</div>
                    <div className="text-xs text-secondary-text mt-1">Draws always go the full five rounds</div>
                  </div>
                )}
                
                {/* Decision - Always goes the distance */}
                {selectedWinner !== 'draw' && selectedWinner !== 'no_contest' && method.includes('decision') && (
                  <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                    <div className="font-medium text-green-400">Decision - Went the Distance</div>
                    <div className="text-xs text-secondary-text mt-1">Decisions always go the full five rounds</div>
                  </div>
                )}
                
                {/* KO/TKO - Never goes the distance */}
                {method === 'ko_tko' && (
                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-4">
                    <div className="font-medium text-red-400">KO/TKO - Did Not Go the Distance</div>
                    <div className="text-xs text-secondary-text mt-1">Enter the round and time below</div>
                  </div>
                )}
                
                {/* Submission - Never goes the distance */}
                {method === 'submission' && (
                  <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4 mb-4">
                    <div className="font-medium text-orange-400">Submission - Did Not Go the Distance</div>
                    <div className="text-xs text-secondary-text mt-1">Enter the round and time below</div>
                  </div>
                )}
                
                {/* DQ - Never goes the distance, counts as KO */}
                {method === 'dq' && (
                  <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4 mb-4">
                    <div className="font-medium text-purple-400">DQ - Did Not Go the Distance</div>
                    <div className="text-xs text-secondary-text mt-1">DQ counts as a KO/TKO for betting purposes</div>
                  </div>
                )}

                {/* Round and Time inputs - Show for DQ or if fight did not go the distance (but not for no_contest) */}
                {wentTheDistance === false && method !== 'no_contest' && selectedWinner !== 'no_contest' && (
                  <div className="mt-4 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-primary-text mb-2">
                          Round fight ended in
                        </label>
                        <select
                          value={round}
                          onChange={(e) => setRound(e.target.value)}
                          className="w-full px-3 py-2 bg-tertiary-bg border border-border rounded-lg text-primary-text"
                        >
                          <option value="">Select round</option>
                          <option value="1">Round 1</option>
                          <option value="2">Round 2</option>
                          <option value="3">Round 3</option>
                          <option value="4">Round 4</option>
                          <option value="5">Round 5</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-primary-text mb-2">
                          Time in round
                        </label>
                        <Input
                          type="text"
                          placeholder="MM:SS (e.g., 1:53)"
                          value={timeInRound}
                          onChange={(e) => setTimeInRound(e.target.value)}
                          helperText="Format: MM:SS (e.g., 1:53, 5:00)"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {error && (
              <div className="p-3 bg-error/10 border border-error rounded-lg">
                <p className="text-sm text-error">{error}</p>
              </div>
            )}

            <div className="flex gap-3 pt-4 border-t border-border">
              <Button
                type="button"
                variant="secondary"
                fullWidth
                onClick={() => navigate(`/admin/${eventId}`)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                fullWidth
                disabled={!selectedWinner}
              >
                Enter Result
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
}

